import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import { adminAPI } from '../../services/api';

interface Submission {
  id: number;
  title: string;
  faculty_name: string;
  category_id?: number;
  category_name: string;
  status: string;
  submitted_date: string;
}

interface DashboardStats {
  total_submissions: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  total_faculty: number;
  total_admins: number;
}

interface CategoryBreakdown {
  id: number;
  category_name: string;
  submission_count: number;
}

export default function AdminDashboard() {
  const [statsData, setStatsData] = useState<DashboardStats>({
    total_submissions: 0,
    pending_count: 0,
    approved_count: 0,
    rejected_count: 0,
    total_faculty: 0,
    total_admins: 0,
  });
  const [recentSubmissions, setRecentSubmissions] = useState<Submission[]>([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState<CategoryBreakdown[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [dashboardResult, approvedResult] = await Promise.all([
          adminAPI.getDashboardStats(),
          adminAPI.getAllSubmissions(undefined, 'approved', undefined, 1, 5000),
        ]);

        if (!dashboardResult.error && dashboardResult.data) {
          const data = dashboardResult.data;
          if (Array.isArray(data) && data.length >= 3) {
            setStatsData(prev => data[0][0] || prev);
            setRecentSubmissions(data[2] || []);
          }
        }

        if (!approvedResult.error && approvedResult.data) {
          const approvedData = approvedResult.data;
          const approvedSubmissions: Submission[] = Array.isArray(approvedData) && approvedData.length >= 2
            ? approvedData[0] || []
            : Array.isArray(approvedData)
              ? approvedData
              : [];

          const groupedByCategory = approvedSubmissions.reduce<Record<string, CategoryBreakdown>>((acc, item, index) => {
            const key = item.category_name || 'Uncategorized';
            if (!acc[key]) {
              acc[key] = {
                id: item.category_id || index + 1,
                category_name: key,
                submission_count: 0,
              };
            }
            acc[key].submission_count += 1;
            return acc;
          }, {});

          setCategoryBreakdown(Object.values(groupedByCategory));
        }
      } catch (err) {
        console.error('Failed to fetch dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const stats = [
    {
      label: 'Total Submissions',
      value: statsData.total_submissions.toString(),
      icon: FileText,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
    },
    {
      label: 'Pending Review',
      value: statsData.pending_count.toString(),
      icon: Clock,
      color: 'bg-amber-500',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
    },
    {
      label: 'Approved',
      value: statsData.approved_count.toString(),
      icon: CheckCircle,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
    },
    {
      label: 'Rejected',
      value: statsData.rejected_count.toString(),
      icon: XCircle,
      color: 'bg-red-500',
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
    },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
            <Clock className="w-4 h-4 mr-1" />
            Pending
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">
            Welcome to the IMDC Creative Works Management System
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 ${stat.bgColor} rounded-lg flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 ${stat.textColor}`} />
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
              <div className="text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Pending Reviews Alert */}
        {statsData.pending_count > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-start">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="font-semibold text-amber-900 mb-1">
                  {statsData.pending_count} Submissions Awaiting Review
                </h3>
                <p className="text-amber-700 text-sm">
                  You have new creative works that need your attention.
                </p>
              </div>
              <Link
                to="/admin/submissions"
                className="ml-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium whitespace-nowrap"
              >
                Review Now
              </Link>
            </div>
          </div>
        )}

        {/* Recent Submissions */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Recent Submissions</h2>
                <p className="text-gray-600 mt-1">Latest creative works awaiting review</p>
              </div>
              <Link
                to="/admin/submissions"
                className="text-purple-600 hover:text-purple-700 font-medium text-sm"
              >
                View All →
              </Link>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {loading ? (
              <div className="p-6 text-center text-gray-500">Loading...</div>
            ) : recentSubmissions.length > 0 ? (
              recentSubmissions.map((submission) => (
                <Link
                  key={submission.id}
                  to={`/admin/submissions/${submission.id}`}
                  className="block p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{submission.title}</h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span>{submission.faculty_name}</span>
                        <span>•</span>
                        <span>{submission.category_name}</span>
                        <span>•</span>
                        <span>{new Date(submission.submitted_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="ml-4">{getStatusBadge(submission.status)}</div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="p-6 text-center text-gray-500">No pending submissions</div>
            )}
          </div>
        </div>

        {/* Analytics */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Category Breakdown */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Submissions by Category</h3>
            </div>
            <div className="space-y-4">
              {categoryBreakdown.map((item, index) => (
                <div key={item.id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">{item.category_name}</span>
                  <span className="text-sm font-semibold text-gray-900">{item.submission_count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* System Stats */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">System Overview</h3>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Total Faculty Members</span>
                <span className="font-semibold text-gray-900">{statsData.total_faculty}</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Total Admins</span>
                <span className="font-semibold text-green-600">{statsData.total_admins}</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Total Submissions</span>
                <span className="font-semibold text-gray-900">{statsData.total_submissions}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
